// File: src/components/EditBookmarkModal.jsx
import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import {
  fetchBookmarkGroups,
  createBookmarkGroup,
  getBookmark,
  saveBookmark,
  deleteBookmark,
} from "../api";
import { sanitize } from "../utils";

/**
 * Props:
 * - user: current user object (required)
 * - question: the question object (required)
 * - onClose: () => void
 * - onSaved: (info) => void  // optional callback after saves/deletes
 *
 * Behavior:
 * - Multi-select shows groups the user has.
 * - Existing groups that already contain the bookmark are pre-selected.
 * - Creating a new group will create it and auto-save bookmark there.
 * - Deselecting a group will immediately delete the bookmark doc from that group.
 * - Clicking "Save" will ensure the bookmark exists in all currently selected groups.
 */
export default function EditBookmarkModal({
  user,
  question,
  onClose,
  onSaved,
}) {
  const uid = user?.uid;
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]); // { value, label }[]
  const [existingMap, setExistingMap] = useState({}); // groupId -> bookmarkDoc (if exists)
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  // deterministic bookmark id
  const bookmarkId =
    question && question.chapter && question.assignment && question.qno
      ? `${sanitize(question.chapter)}__${sanitize(question.assignment)}__${
          question.qno
        }`
      : null;

  useEffect(() => {
    if (!uid) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const g = await fetchBookmarkGroups(uid);
        if (!mounted) return;
        setGroups(g || []);

        if (!bookmarkId) {
          setSelectedGroups([]);
          setExistingMap({});
          return;
        }

        // Check existence in parallel
        const checks = await Promise.all(
          (g || []).map(async (grp) => {
            const b = await getBookmark(uid, grp.id, bookmarkId);
            return { group: grp, bookmark: b };
          })
        );

        const preselected = checks
          .filter((c) => c.bookmark)
          .map((c) => ({ value: c.group.id, label: c.group.name }));

        const map = {};
        checks.forEach((c) => {
          if (c.bookmark) map[c.group.id] = c.bookmark;
        });

        setExistingMap(map);
        // default to preselected if exists, else keep none selected
        setSelectedGroups(preselected);
      } catch (e) {
        console.error("Failed to load bookmark groups", e);
        setError("Failed to load bookmark groups");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, bookmarkId]);

  if (!user || !question) return null;

  function groupOptions() {
    return (groups || []).map((g) => ({ value: g.id, label: g.name }));
  }

  // create payload for saving
  function makePayload() {
    return {
      chapter: question.chapter,
      assignment: question.assignment,
      qno: question.qno,
      questionImage: question.questionImage || null,
      answerKey: question.answerKey ?? null,
      questionType: question.questionType ?? null,
      answerKeyCandidates: question.answerKeyCandidates || null,
      isSolutionAvailable: !!question.isSolutionAvailable,
      solutionImage: question.solutionImage || null,
      savedFromTestId: question.testId || null,
      savedAt: new Date(),
    };
  }

  // called when user creates a new group via creatable select
  async function handleCreateGroup(name) {
    if (!uid) return;
    setWorking(true);
    setError("");
    try {
      const grp = await createBookmarkGroup(uid, name);
      // update local groups and selection
      setGroups((prev) => [grp, ...(prev || [])]);
      const newSel = { value: grp.id, label: grp.name };
      setSelectedGroups((prev) => [...(prev || []), newSel]);

      // auto-save bookmark to the newly created group
      if (bookmarkId) {
        const saved = await saveBookmark(
          uid,
          grp.id,
          bookmarkId,
          makePayload()
        );
        setExistingMap((m) => ({ ...m, [grp.id]: saved }));
        onSaved &&
          onSaved({
            action: "created-group-and-saved",
            group: grp,
            bookmark: saved,
          });
      }
    } catch (e) {
      console.error("Create group failed", e);
      setError("Failed to create group");
    } finally {
      setWorking(false);
    }
  }

  // called when selection changes (including deselect)
  // newValue: array of { value,label } or null
  async function handleSelectionChange(newValue) {
    const newSel = newValue || [];
    // determine removed group ids: present in existingMap or previous selection, but not in newSel
    const prevIds = new Set((selectedGroups || []).map((s) => s.value));
    const newIds = new Set(newSel.map((s) => s.value));

    const removed = Array.from(prevIds).filter((id) => !newIds.has(id));

    // determine newly added (but not created via onCreateOption)
    const added = Array.from(newIds).filter((id) => !prevIds.has(id));

    setSelectedGroups(newSel);

    if (removed.length === 0) {
      // nothing to delete
      return;
    }

    // delete bookmarks from removed groups
    setWorking(true);
    setError("");
    try {
      await Promise.all(
        removed.map(async (gid) => {
          try {
            await deleteBookmark(uid, gid, bookmarkId);
            // update existingMap
            setExistingMap((m) => {
              const copy = { ...m };
              delete copy[gid];
              return copy;
            });
            onSaved && onSaved({ action: "deleted-from-group", groupId: gid });
          } catch (e) {
            // log and continue; we don't want one failure to block others
            console.warn("Failed to delete bookmark from group", gid, e);
          }
        })
      );
    } catch (e) {
      console.error("Error deleting bookmarks on deselect", e);
      setError("Failed to remove bookmark from some groups");
    } finally {
      setWorking(false);
    }

    // Note: newly added groups (non-created) will be saved when user clicks Save (or you can auto-save here).
    // For this implementation we will auto-save newly added groups immediately for a snappier UX:
    if (added.length > 0) {
      setWorking(true);
      try {
        await Promise.all(
          added.map(async (gid) => {
            // skip if already existed
            if (existingMap[gid]) return;
            try {
              const saved = await saveBookmark(
                uid,
                gid,
                bookmarkId,
                makePayload()
              );
              setExistingMap((m) => ({ ...m, [gid]: saved }));
              onSaved &&
                onSaved({
                  action: "saved-to-group",
                  groupId: gid,
                  bookmark: saved,
                });
            } catch (e) {
              console.warn("Failed to save to newly-added group", gid, e);
            }
          })
        );
      } catch (e) {
        console.error(e);
      } finally {
        setWorking(false);
      }
    }
  }

  // Save button ensures bookmark exists in all currently selected groups (useful if auto-save skipped some)
  async function handleSaveAll() {
    if (!bookmarkId) return;
    setWorking(true);
    setError("");
    try {
      const toSave = (selectedGroups || []).map((g) => g.value).filter(Boolean);
      await Promise.all(
        toSave.map(async (gid) => {
          if (existingMap[gid]) return; // already present
          try {
            const saved = await saveBookmark(
              uid,
              gid,
              bookmarkId,
              makePayload()
            );
            setExistingMap((m) => ({ ...m, [gid]: saved }));
            onSaved &&
              onSaved({
                action: "saved-to-group",
                groupId: gid,
                bookmark: saved,
              });
          } catch (e) {
            console.warn("Failed saving to group", gid, e);
          }
        })
      );
      // optionally close modal after save
      onClose && onClose();
    } catch (e) {
      console.error("Failed to save bookmarks", e);
      setError("Save failed");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 overflow-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Save Bookmark</h3>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={() => onClose && onClose()}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="text-sm text-gray-700 mb-2">
          {question.chapter} / {question.assignment} — Q{question.qno}
        </div>

        <label className="block text-sm mb-3">
          <span className="text-yellow-700">
            Choose groups (create new or pick multiple). Deselecting a group
            will remove the bookmark from that group.
          </span>
          <CreatableSelect
            isMulti
            isClearable
            options={groupOptions()}
            value={selectedGroups}
            onChange={handleSelectionChange}
            isDisabled={working}
            onCreateOption={(inputValue) => handleCreateGroup(inputValue)}
            placeholder={
              loading ? "Loading groups..." : "Select or create group(s)"
            }
            className="mt-2 text-base"
          />
        </label>

        {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
        {working && (
          <div className="text-sm text-gray-500 mb-2">Applying changes…</div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => onClose && onClose()}
            className="px-3 py-2 rounded border"
            disabled={working}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            className="px-4 py-2 rounded bg-blue-600 text-white"
            disabled={working}
          >
            {working ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
